import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Eye, Wind, Ear, Brain, Target, 
  CheckCircle, Clock, Waves, TreePine, Footprints, Heart,
  BookOpen, Crosshair, Activity
} from 'lucide-react';
import Header from '../components/Header';
import QMGSymbols from '../components/QMGSymbols';
import Footer from '../components/Footer';

const SKILLS = [
  {
    icon: Wind,
    title: 'Breath Awareness',
    description: 'Your breath is the anchor that brings you back to the present moment. When tension rises on the course, your breathing is the first thing to change — and the first thing you can control.',
    onCourse: 'Use a 4-4-4 box breath (inhale 4 seconds, hold 4, exhale 4) before every pre-shot routine. This activates your parasympathetic nervous system and lowers cortisol.',
    offCourse: 'Practise 5-10 minutes of focused breathing daily. Start with simply counting breaths to 10, then restarting. Notice when your mind wanders — that noticing IS mindfulness.',
    inApp: 'Log breathing practice under Pillars of Performance → Mindset. Track consistency over time.',
  },
  {
    icon: Eye,
    title: 'Body Scan Awareness',
    description: 'Most golfers don\'t realise they\'re carrying tension until it\'s already affected their swing. A body scan teaches you to detect tension in real-time — jaw, shoulders, grip, stance — before it compounds.',
    onCourse: 'Between shots, scan from head to feet. Release any tension you find. Pay particular attention to your grip pressure, jaw, and shoulders — the three most common tension traps in golf.',
    offCourse: 'Practise a full body scan for 10 minutes before bed. Start at your toes and work upward, noticing sensations without trying to change them.',
    inApp: 'Record body awareness observations in your Diary. Tag entries with "Awareness" to track how often you\'re applying this skill.',
  },
  {
    icon: Ear,
    title: 'Present-Moment Focus',
    description: 'The mind naturally drifts to the past (that missed putt on 7) or the future (what if I bogey 18?). Awareness is the skill of noticing this drift and returning to now — the only shot that matters.',
    onCourse: 'Use sensory anchors: feel your feet on the ground, hear the wind, see the target. These bring you back to the present without forcing concentration.',
    offCourse: 'Practise mindful walking for 5 minutes. Notice each footfall, the temperature of the air, sounds around you. When your mind wanders, gently return.',
    inApp: 'Rate your present-moment focus in Performance Scorecards under "During the Round" and "In-Between Shots" categories.',
  },
  {
    icon: Waves,
    title: 'Thought Observation',
    description: 'You are not your thoughts. Awareness teaches you to observe thoughts as passing events — like clouds moving across a sky — rather than facts you must react to. "I\'m going to miss this" is a thought, not a prediction.',
    onCourse: 'When a negative thought arises, label it: "That\'s a worry thought." This creates psychological distance. You don\'t need to argue with it or replace it — just see it for what it is.',
    offCourse: 'Sit quietly for 5 minutes and watch your thoughts without engaging. Imagine each thought as a leaf floating down a stream. Let it pass.',
    inApp: 'Journal thought patterns in your Diary after rounds. Over time, you\'ll spot recurring patterns and develop faster recognition.',
  },
  {
    icon: Footprints,
    title: 'Walking Meditation Between Shots',
    description: 'The walk between shots is where most mental damage happens — replaying mistakes, catastrophising, or losing focus. Turning this walk into a mindful reset is one of the most powerful skills in competitive golf.',
    onCourse: 'After each shot (good or bad), take 5 mindful steps. Feel each footfall. Let the previous shot go. Then shift into "gather mode" — reading the next shot with fresh eyes.',
    offCourse: 'Practise walking meditation for 10 minutes. Walk slowly, feeling each phase: lifting, moving, placing. This builds the muscle memory for on-course resets.',
    inApp: 'Track your "In-Between Shots" score on Performance Scorecards. This directly measures your ability to reset between shots.',
  },
];

const MACMindfulPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-[#2C5530] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/mac-research" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Research
            </Link>
            <p className="text-white/50 text-sm uppercase tracking-[0.2em] mb-2">Step One</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-4">
              Step Back — Awareness
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              Recognise when you're in an unhelpful mental state. Make the conscious decision to not let the moment own you. This is the foundation of performing under pressure.
            </p>
            <div className="mt-8">
              <QMGSymbols active="awareness" tone="light" size="md" />
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* What It Means */}
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">What Awareness Means in Golf</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                Awareness in golf isn't about emptying your mind or achieving some zen-like calm. It's about <strong>awareness</strong> — noticing what's happening inside you (thoughts, emotions, physical tension) without being controlled by it.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-4">
                When you stand over a 4-foot putt to win a match, your body will react. Your heart rate will rise, your hands may tremble, your mind will race. Awareness doesn't stop these reactions — it gives you the ability to <strong>notice them without being hijacked by them</strong>.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                As Gardner and Moore's research demonstrates, trying to suppress or control these internal states often makes them worse. The mindful golfer observes them, accepts their presence, and refocuses on the task at hand.
              </p>
            </div>
          </section>

          {/* Research Quote */}
          <section className="pb-12 md:pb-16" data-testid="research-quote">
            <div className="bg-[#F2F0E9] border-l-4 border-[#2C5530] p-6 md:p-8">
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "Optimal performance does not require the reduction or control of internal states but rather a non-judging, moment-to-moment awareness and acceptance of one's internal state, whatever that may be."
              </blockquote>
              <p className="text-[#737373] text-sm">— Gardner & Moore, Theoretical & Empirical Developments of MAC, 2012</p>
            </div>
          </section>

          {/* Skills to Train */}
          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Practical mindfulness skills you can develop on and off the course.</p>

            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#2C5530] text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <skill.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                        <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-[#2C5530]/5 p-4">
                        <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                      </div>
                      <div className="bg-[#BC562B]/5 p-4">
                        <p className="text-xs text-[#BC562B] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                      </div>
                      <div className="bg-[#1A1A1A]/5 p-4">
                        <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                        <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Daily Practice */}
          <section className="pb-12 md:pb-16" data-testid="daily-practice">
            <div className="bg-[#2C5530] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-6 text-white">A Simple Daily Awareness Routine</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white/60 text-sm uppercase tracking-wider mb-3">Morning (5 minutes)</h4>
                  <ul className="space-y-2">
                    {['Sit quietly. Close your eyes.', 'Focus on your breath for 2 minutes.', 'Body scan from toes to head — release tension.', 'Set one intention for the day.'].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/90 text-sm"><CheckCircle className="w-4 h-4 text-white/50 flex-shrink-0 mt-0.5" />{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-white/60 text-sm uppercase tracking-wider mb-3">Evening (5 minutes)</h4>
                  <ul className="space-y-2">
                    {['Reflect on one moment you were fully present today.', 'Notice any tension you\'re still carrying. Let it go.', 'Acknowledge one thing you did well.', 'Write a diary entry in QMG if it was a practice/round day.'].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/90 text-sm"><CheckCircle className="w-4 h-4 text-white/50 flex-shrink-0 mt-0.5" />{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Navigation to other MAC pages */}
          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/mac/accepting" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/50 hover:shadow-md transition-all group" data-testid="nav-accepting">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Next Step</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#BC562B] transition-colors">Step Up — Accepting</h3>
                <p className="text-sm text-[#57534E]">Learn to accept discomfort as normal and see challenges as opportunities.</p>
                <ArrowRight className="w-5 h-5 text-[#BC562B] mt-3" />
              </Link>
              <Link to="/mac/commitment" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/50 hover:shadow-md transition-all group" data-testid="nav-commitment">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Step Three</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#BC562B] transition-colors">Step In — Action</h3>
                <p className="text-sm text-[#57534E]">Commit fully to your process and personal values on every shot.</p>
                <ArrowRight className="w-5 h-5 text-[#BC562B] mt-3" />
              </Link>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MACMindfulPage;
